import React from 'react';
import { useTable, Column, Row as RTRow } from 'react-table';

import {
  Table, TableBody, TableHead, TableRow, TableCell, Box
} from '@mui/material';


import { TableProps } from '../Table';
import { NoItemsOverlayBody } from '../Table/Overlay';
import { Loader } from '../Loader';
import { CollapsibleListRow } from './CollapseListRow.component';
import { useElementInView } from '@uplift/hooks';


export type CollapseListProps<Row extends Record<string, unknown>> = TableProps<Row> & {
  headerColumnsCount?: number;
  disableCollapsibleRowFixedTableLayout?: boolean;
};


export function CollapseList<Row extends Record<string, unknown>>({
  rows: data,
  columns,
  loading,
  onInfiniteScroll,
  headerColumnsCount = 2,
  disableCollapsibleRowFixedTableLayout,
  ...props
}: CollapseListProps<Row>) {
  const hasInfiniteScroll = !!onInfiniteScroll;
  const _columns: readonly Column<Row>[] = React.useMemo(() => {
    return columns.map<Column<Row>>((c, index) => {
      const res: Column<Row> = {
        id: index.toString(),
        accessor: c.field,
        Header: c.headerName,
        minWidth: c.minWidth,
        width: c.width,
      };

      if (c?.renderCell) {
        res.Cell = (props) => {
          return c.renderCell?.({ row: props.cell.row.original, value: props.cell.value });
        };
      }

      return res;
    });
  }, [ columns ]);

  const {
    headerGroups,
    rows,
    prepareRow,
    ...table
  } = useTable({
    columns: _columns,
    data: data,
  });

  const tableRef = React.useRef<HTMLTableElement | null>(null);
  const loadedRef = React.useRef<Record<string, boolean>>({});

  const {
    elementRef,
    inView
  } = useElementInView<HTMLTableRowElement>({ options: {}, disabled: !hasInfiniteScroll, initialViewState: false });


  React.useEffect(() => {
    if (inView) {
      const currentKey = data.map(d => d.id).join(',');

      if (loadedRef.current[currentKey]) return;
      loadedRef.current[currentKey] = true;
      onInfiniteScroll?.();
    }
  }, [ inView ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return function from effect function to Run on unmount
  React.useEffect(() => () => {
    loadedRef.current = {};
  }, []);

  if (!rows.length) {
    return <NoItemsOverlayBody icon={props.emptyIcon} message={props.emptyMessage} />;
  }

  return (
    <Table
      {...table.getTableProps()}
      size="small"
      style={{ tableLayout: 'fixed', width: '100%' }}
      ref={tableRef}
    >
      <TableHead>
        {headerGroups.map((headerGroup) => {
          const { key, ...hgProps } = headerGroup.getHeaderGroupProps();

          return (
            <TableRow key={key} {...hgProps}>
              {headerGroup.headers.slice(0, headerColumnsCount).map((column) => {
                const { key, ...hProps } = column.getHeaderProps();

                return (
                  <TableCell key={key} {...hProps} style={{ width: column.width, paddingRight: 0, }}>
                    {column.render('Header')}
                  </TableCell>
                );
              })}
              <TableCell style={{ width: 48 }} />
            </TableRow>
          );
        })}
      </TableHead>
      <TableBody {...table.getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          const { key, ...rowProps } = row.getRowProps();

          return (
            <CollapsibleListRow
              key={key}
              row={row as RTRow<Record<string, unknown>>}
              tableColumnsCount={headerColumnsCount}
              disableCollapsibleRowFixedTableLayout={disableCollapsibleRowFixedTableLayout}
              {...rowProps}
            />
          );
        })}
        {hasInfiniteScroll && (
          <TableRow ref={elementRef}>
            <TableCell style={{ borderBottom: 0 }} colSpan={headerColumnsCount + 1}>
              {loading ? (
                <Loader width="100%" color="primary" size="24px" thickness={5} mt={2} />
              ) : (
                <Box height="24px" mt={2} />
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
